import { useT } from "../utilities/TranslatedText";
import classNames from "classnames";
import { observer } from "mobx-react";
import dynamic from "next/dynamic";
import FlatIcon from "../utilities/FlatIcon";
import FontAwesome from "../utilities/FontAwesome";
import styles from "./styles/FooterCS.module.scss";
import MailToLink from "../misc/MailToLink";
import ClickableLink from "../utilities/ClickableLink";
import { getDirectionsUrl } from "../../utils/SchoolBlocksUtilities";
import { useStores } from "../../stores/useStores";
import { Heading } from "@radix-ui/themes";

const PhoneNumber = dynamic(() => import("../utilities/PhoneNumber"));

const addressColumnClassName = classNames({
  [styles.addressColumn]: true,
});

const FooterAddressColumn = observer(() => {
  const t = useT();
  const { organizationStore } = useStores();

  const location =
    organizationStore.organization.locations?.find((l) => l.is_primary) ??
    organizationStore.organization.locations?.[0];

  let locationBlock: React.ReactNode = null;
  if (location) {
    const fullAddress = [
      location.address,
      location.address2,
      [location.city ? `${location.city},` : "", location.state, location.zip]
        .filter(Boolean)
        .join(" "),
    ]
      .filter(Boolean)
      .join(", ");
    const mapsHref = getDirectionsUrl(
      organizationStore.organization.title,
      location,
    );

    locationBlock = (
      <address className={"notranslate"}>
        <ClickableLink
          href={mapsHref}
          aria-label={`${fullAddress}, get directions on Google Maps (opens in a new tab)`}
        >
          {location.address && (
            <>
              {location.address}
              <br />
            </>
          )}
          {location.address2 && (
            <>
              {location.address2}
              <br />
            </>
          )}
          {location.city ? `${location.city}, ` : ""}
          {location.state ? `${location.state} ` : ""}
          {location.zip ? location.zip : ""}
        </ClickableLink>
      </address>
    );
  }

  return (
    <div className={addressColumnClassName}>
      <Heading as={"h3"} size={"5"}>
        {organizationStore.organization.title}
      </Heading>
      {locationBlock && (
        <div>
          <div>
            <FlatIcon ariaHidden={true} name={"flaticon-map"} />
          </div>
          <div>{locationBlock}</div>
        </div>
      )}
      {locationBlock && location.phone && (
        <div>
          <div>
            <FlatIcon
              title="Primary Phone Number"
              ariaHidden={true}
              name={"flaticon-telephone-of-old-design"}
            />
          </div>
          <div>
            <PhoneNumber
              ariaLabel={t("Call primary phone number")}
              isLinked
              number={location.phone}
              description={location.phone_description}
            />
          </div>
        </div>
      )}
      {locationBlock && location.phone2 && (
        <div>
          <div>
            <FlatIcon
              title="Secondary Phone Number"
              ariaHidden={true}
              name={"flaticon-telephone-of-old-design"}
            />
          </div>
          <div>
            <PhoneNumber
              ariaLabel={t("Call secondary phone number")}
              isLinked
              number={location.phone2}
              description={location.phone2_description}
            />
          </div>
        </div>
      )}
      {locationBlock && location.fax && (
        <div>
          <div>
            <FontAwesome
              title="Fax"
              ariaHidden={true}
              name={"fa-print"}
              prefix={"fas"}
            />
          </div>
          <div className={styles.faxNumber}>
            <span>{t("Fax")}: </span>
            <PhoneNumber
              number={location.fax}
              description={location.fax_description}
            />
          </div>
        </div>
      )}
      {locationBlock && location.email && (
        <div>
          <div>
            <FontAwesome
              title="Email"
              ariaHidden={true}
              name={"fa-at"}
              prefix={"fas"}
            />
          </div>
          <div>
            <MailToLink title={"Email"} recipient={location.email}>
              {location.email}
            </MailToLink>
          </div>
        </div>
      )}
    </div>
  );
});

export default FooterAddressColumn;
